/// <amd-module name="CodeCoverage/CodeCoverageTab/Components/CodeCoverageDetailsList" />
import { ICodeCoverageModuleDetails } from 'Actions/CodeCoverageTabActionHub';
import * as Resources from 'CodeCoverage/CodeCoverageTab/Resources';
import { CoveredBarChart } from 'Components/CoveredBarChart';
import { ColumnActionsMode, DetailsList, IColumn, IDetailsListProps, SelectionMode } from 'OfficeFabric/DetailsList';
import { Link } from 'VSSUI/Link';
import * as React from 'react';
import { VssComponent, IVssComponentContext } from 'VSS/Platform/Layout';
import { IVssTelemetryService } from 'VSS/Platform/Telemetry';
import { localeIgnoreCaseComparer } from 'VSS/Core/Util/String';
import { TelemetryEvents } from 'CodeCoverage/Common/Constants';


// Width of coverage visual column in pixels
const COVERAGE_VISUAL_WIDTH = 280;

const numberComparer = (a: number, b: number) => (a-b);

enum ColumnKeys {
    Modules,
    CoveredLines,
    TotalLines,
    LineCoverage,
    CoverageVisual
}

export interface ICodeCoverageDetailsListProps {
    codeCoverageModuleList: Array<ICodeCoverageModuleDetails>;
    moduleFilterText: string;
}

export interface ICodeCoverageDetailsListState {
    sortedColumnKey: string;
    isSortedDescending: boolean;
    listItems: ICodeCoverageModuleDetails[];
}

export class CodeCoverageDetailsList extends VssComponent<ICodeCoverageDetailsListProps, ICodeCoverageDetailsListState> {
    private _telemetryService: IVssTelemetryService;

    constructor(props: ICodeCoverageDetailsListProps, context: IVssComponentContext) {
        super(props, context);

        // Sort by module names by default
        const listItems = this._sortItems(this._generateItemList(), this._getComparer(ColumnKeys[ColumnKeys.Modules]), false);

        this.state = {
            sortedColumnKey: ColumnKeys[ColumnKeys.LineCoverage],
            isSortedDescending: false,
            listItems: listItems
        };

        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    //Method to update the state when props are changed
    public componentDidUpdate(prevProps: ICodeCoverageDetailsListProps, prevState: ICodeCoverageDetailsListState) {
        super.componentDidUpdate(prevProps, prevState);
        if (this.props.codeCoverageModuleList !== prevProps.codeCoverageModuleList) {
            const listItems = this._sortItems(this._generateItemList(), this._getComparer(ColumnKeys[ColumnKeys.Modules]), false);
            this.setState({
                sortedColumnKey: ColumnKeys[ColumnKeys.Modules],
                isSortedDescending: false,
                listItems: listItems,
            } as ICodeCoverageDetailsListState);
        }
    }

    private _renderColumn = (item: ICodeCoverageModuleDetails, index: number, column: IColumn): JSX.Element => {
        switch (column.key) {
            case ColumnKeys[ColumnKeys.Modules]:
                if(item.moduleUrl) {
                    return <Link className="coverage-module-link" href={item.moduleUrl}>{item.moduleName}</Link>;
                }
                else{
                    return <span>{item.moduleName}</span>
                }
            case ColumnKeys[ColumnKeys.CoveredLines]:
                return <span>{item.coveredLines}</span>;
            case ColumnKeys[ColumnKeys.TotalLines]:
                return <span>{item.totalLines}</span>;
            case ColumnKeys[ColumnKeys.LineCoverage]:
                return <span>{item.lineCoverage}</span>;
            case ColumnKeys[ColumnKeys.CoverageVisual]:
                return (
                    <CoveredBarChart
                        coveredPercentage={ item.totalLines ? (item.coveredLines * (100) / item.totalLines) : 0 }
                        width={ COVERAGE_VISUAL_WIDTH }
                    />
                );
        }

        return <div />;
    }
        
    private _generateItemList(): Array<ICodeCoverageModuleDetails> {
        return this.props.codeCoverageModuleList.filter((data) => data instanceof Object);
    }
    
    private _getFilteredList(): ICodeCoverageModuleDetails[] {
        const coverageList = this.state.listItems;
        const filter = this.props.moduleFilterText;

        if (filter.trim() === "" ) {
            return coverageList;
        }

        return coverageList.filter((item: ICodeCoverageModuleDetails) => (item.moduleName.toLocaleLowerCase().indexOf(filter.toLocaleLowerCase().trim()) >= 0));
    }

    private _getColumnList(): Array<IColumn> {
        return [
            this._createColumn(ColumnKeys[ColumnKeys.Modules], Resources.ModuleCoverageText, this._renderColumn),
            this._createColumn(ColumnKeys[ColumnKeys.CoveredLines], Resources.CoveredLinesText, this._renderColumn),
            this._createColumn(ColumnKeys[ColumnKeys.TotalLines], Resources.TotalCoverageLinesText, this._renderColumn),
            this._createColumn(ColumnKeys[ColumnKeys.LineCoverage], Resources.LineCoverageText, this._renderColumn),
            this._createColumn(ColumnKeys[ColumnKeys.CoverageVisual], Resources.CoverageVisualText, this._renderColumn, COVERAGE_VISUAL_WIDTH)
        ];
    }

    private _createColumn(key: string, name: string, onRender: (item: ICodeCoverageModuleDetails, index: number, column: IColumn) => JSX.Element, minWidth?: number): IColumn {
        return {
            fieldName: key,
            key,
            name,
            onRender,
            onColumnClick: this._onColumnClick,
            minWidth: minWidth || 100,
            isResizable: true,
            isSorted: this.state.sortedColumnKey === key,
            isSortedDescending: this.state.isSortedDescending,
            isPadded: true,
            columnActionsMode: ColumnActionsMode.clickable,
            isRowHeader: false,
            className: 'code-coverage-details-list-cell'
        } as IColumn;
    }

    private _onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {    
        
        const comparer = this._getComparer(column.key);
        if (!comparer) {
            return;
        }

        const isSortedDescending = this.state.sortedColumnKey === column.key && !column.isSortedDescending;

        this.setState({
            sortedColumnKey: column.key,
            isSortedDescending,
            listItems: this._sortItems(this.state.listItems, comparer, isSortedDescending),
        } as ICodeCoverageDetailsListState);

        this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.DetailsListSortClicked, {
            "Column": column.key,
            "Order": isSortedDescending ? "Descending" : "Ascending"
        });
    }
    
    private _sortItems(items: ICodeCoverageModuleDetails[], ascendingComparer: { (a: ICodeCoverageModuleDetails, b: ICodeCoverageModuleDetails): number }, isSortedDescending: boolean): ICodeCoverageModuleDetails[] {
        const comparer = isSortedDescending
            ? (one: any, another: any) => ascendingComparer(another, one)
            : ascendingComparer;

        return items.slice().sort(comparer);
    }

    private _getComparer(key: string): { (a: ICodeCoverageModuleDetails, b: ICodeCoverageModuleDetails): number } {
        switch (key) {
            case ColumnKeys[ColumnKeys.Modules]:
                return (a, b) => localeIgnoreCaseComparer(a.moduleName, b.moduleName);
            case ColumnKeys[ColumnKeys.CoveredLines]:
                return (a, b) => numberComparer(a.coveredLines, b.coveredLines);
            case ColumnKeys[ColumnKeys.TotalLines]:
                return (a, b) => numberComparer(a.totalLines, b.totalLines);
            case ColumnKeys[ColumnKeys.LineCoverage]:
            case ColumnKeys[ColumnKeys.CoverageVisual]:
            default:
                return (a, b) => numberComparer(parseFloat(a.lineCoverage), parseFloat(b.lineCoverage));
        }
    }

    public render(): JSX.Element {
        const props: IDetailsListProps = {
            items: this._getFilteredList(),
            columns: this._getColumnList(),
            selectionMode: SelectionMode.none,
            className: "code-coverage-details-list",
        };

        return (
            <div className="coverage-details-list-container">
                <DetailsList {...props} />
            </div>
        );
    } 
}
